<template>
  <div class="package-list">
    <!-- === KPI CARDS === -->
    <div class="kpi-row fade-in">
      <div class="kpi-card" v-for="(kpi, i) in packageStats" :key="i">
        <div>
          <h4>{{ kpi.title }}</h4>
          <div class="value">{{ kpi.value }}</div>
        </div>
        <i :class="kpi.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- === FILTERS & ADD NEW PACKAGE === -->
    <div class="grid-header fade-in">
      <div class="filters">
        <div class="search-box">
          <i class="bx bx-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search packages..."
            @keyup.enter="loadPackages"
          />
        </div>

        <select v-model="selectedStatus" @change="loadPackages">
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <button class="btn-primary" @click="addNewPackage">
        <i class="bx bx-plus"></i> New Package
      </button>
    </div>

    <!-- === LOADER (optional UX) === -->
    <transition name="fade-slide" v-if="loading">
      <div class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- === PACKAGE CARDS GRID === -->
    <div v-else class="website-grid fade-in">
      <div
        class="website-card"
        v-for="(pkg, i) in packages"
        :key="i"
      >
        <!-- Header -->
        <div class="website-card-header">
          <h4><i class="bx bx-box"></i> {{ pkg.name }}</h4>

        
        </div>

        <!-- Body -->
        <div class="website-card-body">
          <div class="package-details">
            <div class="detail-item">
              <i class="bx bx-dollar-circle"></i>
              <span><strong>Price:</strong> ${{ pkg.price }}</span>
            </div>
            <div class="detail-item">
              <i class="bx bx-time-five"></i>
              <span><strong>Duration:</strong> {{ pkg.duration }} mins</span>
            </div>
            <div class="detail-item">
              <i class="bx bx-check-circle"></i>
              <span
                :class="{
                  'text-green-600': pkg.status === 'Active',
                  'text-red-500': pkg.status === 'Inactive'
                }"
              >
                <strong>Status:</strong> {{ pkg.status }}
              </span>
            </div>
          </div>

          <p class="pkg-desc">
            {{ pkg.description }}
          </p>
        </div>

        <!-- Footer -->
        <div class="website-card-footer">
          <span class="website-domain">Created: {{ pkg.createdAt }}</span>
          <div class="footer-actions">
            <button class="btn-edit" @click="editPackage(pkg)">
              <i class="bx bx-edit"></i> Edit
            </button>
            <button class="btn-delete" @click="deletePackage(pkg)">
              <i class="bx bx-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>

      <!-- No Packages Placeholder -->
  
      <div v-if="!packages.length && !loading" class="empty-state">
        <i class="bx bx-foldepackage"></i>
        <h3>No packages Found</h3>
        <p>You haven’t created any package yet. Let’s set up your first one.</p>
        <button
          class="btn-primary"
          @click="addNewPackage"
          style="padding: 8px 14px; font-size: 13px; border-radius: 6px;"
        >
          <i class="bx bx-plus" style="font-size: 14px; color: white; margin-top: .7rem;"></i> Create New Packages
        </button>
      </div>

    </div>

    <!-- === PAGINATION === -->
    <div v-if="!loading && packages.length" class="pagination fade-in">
      <span
        >Showing 1–{{ packages.length }} of {{ packages.length }}
        packages</span
      >
      <div class="pagination-buttons">
        <button><i class="bx bx-chevron-left"></i></button>
        <button class="active">1</button>
        <button>2</button>
        <button>3</button>
        <button><i class="bx bx-chevron-right"></i></button>
      </div>
    </div>

    <br /><br />
     <ConfirmDialog
  :show="showConfirm"
  :title="confirmTitle"
  :message="confirmMessage"
  @cancel="showConfirm = false"
  @confirmAction="handleConfirm"
/>
  </div>
</template>

<script scoped>
import { ref, computed, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import {
  getPackages,
  getPackageReport,
  deletePackage,
} from "@/apis/pkgapis";
import ConfirmDialog from "@/comps/ConfirmDialog.vue";


export default {
  name: "PackageList",
  components:{
    ConfirmDialog
  },
  setup() {
    const toast = useToast();
    const showConfirm = ref(false);
    const confirmTitle = ref("");
    const confirmMessage = ref("");
    let confirmCallback = () => {};

    const openConfirm = (title, message, onConfirm) => {
      confirmTitle.value = title;
      confirmMessage.value = message;
      confirmCallback = onConfirm;
      showConfirm.value = true;
    };

    const handleConfirm = () => {
      showConfirm.value = false;
      if (typeof confirmCallback === "function") confirmCallback();
    };
    const searchQuery = ref("");
    const selectedStatus = ref("");
    const packages = ref([]);
    const packageStats = ref([
      { title: "All Packages", value: "0", icon: "bx bx-package" },
      { title: "Active", value: "0", icon: "bx bx-check-circle" },
      { title: "Inactive", value: "0", icon: "bx bx-x-circle" },
    ]);

    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    const userId = userData?.user_id || "";

    const loading = ref(false);
    const page_no = ref(0);

    /* === Computed: Filtered Packages (Local Filtering) === */
    const filteredPackages = computed(() => {
      return packages.value.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
          (!selectedStatus.value || p.status === selectedStatus.value)
      );
    });

    /* === Load Packages (API Fetch) === */
    const loadPackages = async () => {
      try {
        loading.value = true;
        const res = await getPackages(
          userId,
          page_no.value,
          selectedStatus.value,
          searchQuery.value
        );
        if (res?.status && res?.records?.length > 0) {
          packages.value = res.records.map((pkg) => ({
            id: pkg.package_id,
            name: pkg.package_name,
            price: pkg.package_amount,
            description: pkg.package_desc,
            status: pkg.package_status,
            createdAt: new Date(pkg.package_datetime).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            duration: pkg.package_duration || 60,
          }));
        } else {
          packages.value = [];
          toast.success("No packages found.");
        }
      } catch (error) {
        console.error("loadPackages error:", error);
        toast.error("Failed to fetch packages.");
      } finally {
        loading.value = false;
      }
    };

    /* === Load Package Report === */
    const loadPackageReport = async () => {
      try {
        const res = await getPackageReport(userId);
        if (res?.status) {
          packageStats.value = [
            { title: "All Packages", value: res.all_packages || 0, icon: "bx bx-package" },
            { title: "Active", value: res.active_packages || 0, icon: "bx bx-check-circle" },
            { title: "Inactive", value: res.inactive_packages || 0, icon: "bx bx-x-circle" },
          ];
        }
      } catch (error) {
        console.error("loadPackageReport error:", error);
        toast.error("Failed to fetch package report.");
      }
    };

    /* === Delete Package === */
    const handleDeletePackage = async (pkg) => {
      openConfirm(
        "Delete Item",
        `Are you sure you want to delete?`,
        async () => {
          try {
            const res = await deletePackage(pkg.id);
            if (res?.status) {
              toast.success(res.success || "Package deleted successfully!");
              await loadPackages();
              await loadPackageReport();
            } else {
              toast.error(res?.success || "Failed to delete package.");
            }
          } catch (error) {
            console.error("deletePackage error:", error);
            toast.error("Something went wrong while deleting package.");
          }
        }
      );
    };

    /* === Add New Package === */
    const addNewPackage = () => {
      window.location.href = "/new_package";
    };

    /* === Edit Package === */
    const editPackage = (pkg) => {
      sessionStorage.setItem("package_data", JSON.stringify(pkg));
      window.location.href = `/new_package?id=${pkg.id}`;
    };

    /* === Watchers (Realtime Search & Filter Updates) === */
    watch([searchQuery, selectedStatus], async () => {
      await loadPackages();
    });

    /* === Lifecycle === */
    onMounted(async () => {
      await Promise.all([loadPackages(), loadPackageReport()]);
    });

    /* === Return to Template === */
    return {
      searchQuery,
      selectedStatus,
      packages,
      filteredPackages,
      packageStats,
      loading,
      showConfirm,
      confirmTitle,
      confirmMessage,
      openConfirm,
      handleConfirm,
      loadPackages,
      addNewPackage,
      editPackage,
      deletePackage: handleDeletePackage,
    };
  },
};
</script>




<style scoped>
/* === Fade-in Animation === */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(12px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Filters === */
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.filters select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
}

/* === Package Details === */
.package-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--text);
  font-size: 13px;
  margin-bottom: 12px;
}
.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.detail-item i {
  color: var(--primary);
}
.pkg-desc {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.5;
}

/* === Footer Buttons === */
.footer-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-edit,
.btn-delete {
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.btn-edit {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.2);
}
.btn-edit:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}
.btn-delete {
  background: #f3f4f6;
  color: var(--text);
}
.btn-delete:hover {
  background: #fee2e2;
  color: #991b1b;
  transform: translateY(-1px);
}

/* === Dropdown Menu === */
.card-menu {
  position: relative;
}
.card-menu i {
  cursor: pointer;
  font-size: 1.2rem;
}
.card-menu-dropdown {
  position: absolute;
  right: 0;
  top: 1.5rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: none;
  flex-direction: column;
  z-index: 10;
}
.card-menu:hover .card-menu-dropdown {
  display: flex;
}
.card-menu-dropdown button {
  border: none;
  background: none;
  text-align: left;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.25s ease;
}
.card-menu-dropdown button:hover {
  background: #f3f4f6;
}

/* === Pagination === */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
}
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* ==========================================================
   🌐 UNIVERSAL RESPONSIVE CSS FOR DASHBOARD SCREENS
   ========================================================== */

/* === Fade Animation === */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(12px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==========================================================
   📊 KPI CARDS — TOP COUNT ROW RESPONSIVENESS
   ========================================================== */
.kpi-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}
.kpi-card {
  flex: 1 1 calc(25% - 20px);
  min-width: 200px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 12px rgba(0, 0, 0, 0.08);
}
.kpi-card h4 {
  font-size: 14px;
  color: var(--muted);
  margin: 0 0 4px 0;
}
.kpi-card .value {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text);
}
.kpi-icon {
  font-size: 1.8rem;
  color: var(--primary);
  opacity: 0.9;
}

/* ✅ Tablet (≤992px) — 2 cards per row */
@media (max-width: 992px) {
  .kpi-row {
    justify-content: center;
  }
  .kpi-card {
    flex: 1 1 calc(50% - 12px);
    min-width: 240px;
  }
}

/* ✅ Mobile (≤768px) — stacked */
@media (max-width: 768px) {
  .kpi-row {
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
  }
  .kpi-card {
    width: 100%;
    min-width: unset;
    padding: 14px 16px;
  }
  .kpi-card h4 {
    font-size: 13px;
  }
  .kpi-card .value {
    font-size: 1.2rem;
  }
  .kpi-icon {
    font-size: 1.5rem;
  }
}

/* ✅ Extra Small (≤480px) */
@media (max-width: 480px) {
  .kpi-row {
    gap: 10px;
  }
  .kpi-card {
    padding: 12px 14px;
    border-radius: 10px;
  }
  .kpi-card h4 {
    font-size: 12px;
  }
  .kpi-card .value {
    font-size: 1rem;
  }
  .kpi-icon {
    font-size: 1.3rem;
  }
}

/* ==========================================================
   🎛️ FILTERS, GRID HEADER, AND BUTTONS RESPONSIVE FIXES
   ========================================================== */
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.filters select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
}
.grid-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 1.2rem;
}
.grid-header .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.grid-header .btn-primary:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}

/* ==========================================================
   🧱 CARD GRID RESPONSIVENESS (packages, plans, forms, bots)
   ========================================================== */
.website-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}
.website-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 16px;
  transition: all 0.3s ease;
}
.website-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}

/* ==========================================================
   📱 RESPONSIVE BREAKPOINTS FOR CARDS & FILTERS
   ========================================================== */
@media (max-width: 992px) {
  .website-grid {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }
  .filters {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .grid-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .filters {
    width: 100%;
    flex-direction: column;
  }
  .filters select,
  .filters .search-box,
  .grid-header .btn-primary {
    width: 100%;
  }
  .website-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .filters select,
  .filters .search-box input {
    font-size: 12px;
    padding: 5px 8px;
  }
  .website-card {
    padding: 14px;
  }
}

/* ==========================================================
   📄 PAGINATION FIX
   ========================================================== */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
}
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}
/* ==========================================================
   🌀 ADDITIONAL CSS PATCH FOR PACKAGE LIST PAGE
   ========================================================== */

/* === Loader Spinner === */
.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 1.5rem;
  font-size: 15px;
  color: var(--primary);
  font-weight: 600;
  background: #f9fafb;
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}
.loading-spinner i {
  font-size: 1.5rem;
  color: var(--primary);
}

/* === No Data Placeholder === */
.no-data {
  grid-column: 1 / -1;
  text-align: center;
  padding: 2rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #d1d5db;
  color: var(--muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.no-data i {
  font-size: 2.5rem;
  color: #9ca3af;
}
.no-data p {
  font-size: 14px;
  color: var(--text);
}

/* === Status Coloring === */
.text-green-600 {
  color: #059669 !important;
}
.text-red-500 {
  color: #dc2626 !important;
}

/* === Button Hover Enhancements === */
.btn-edit i,
.btn-delete i {
  font-size: 1rem;
}
.btn-edit:hover i {
  transform: rotate(5deg);
}
.btn-delete:hover i {
  transform: scale(1.1);
}

/* === Minor Animation for Cards === */
.website-card {
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.website-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

/* === Tablet Fix: Grid alignment === */
@media (max-width: 992px) {
  .website-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
  }

  .loading-spinner {
    font-size: 14px;
    padding: 1rem;
  }

  .no-data {
    padding: 1.2rem;
  }
}

/* === Mobile Fix: Better readability === */
@media (max-width: 600px) {
  .no-data {
    font-size: 13px;
  }
  .loading-spinner {
    flex-direction: column;
    font-size: 13px;
    text-align: center;
  }
}

/* === Smooth fade animations === */
.fade-in {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>
